import React, { useEffect } from 'react';
import { App } from 'antd';
import { useQuery } from '@apollo/client';
import moment from 'moment';
import { useIntl } from 'react-intl';
import { useLocation, useParams } from 'react-router-dom';

import { renderDataStateIndicator } from '../components/data/dataStateHandlers';
import { QuoteSummaryHeader } from './quote/summary/QuoteSummaryHeader';
import { QUOTE_DETAIL_QUERY } from '../app/graphql/quoteQueries';
import ModalOverlay from '../components/dialogs/ModalOverlay';
import ModalPage from '../templates/ModalPage';
import { useModalPageStyles } from '../templates/ModalPage.styles';
import {
  useQuoteExpirationMessage,
  useQuoteMessage,
} from '../components/dialogs/QuoteRestrictionsMessage';
import { OverrideSettingsContextProvider } from '../components/domainSpecific/settingsElements';
import { useResponsiveQueries } from '../components/responsive/responsiveQueries';
import QuoteContentSmall from './quote/summary/QuoteContentSmall';
import QuoteContentLarge from './quote/summary/QuoteContantLarge';
import { isQuoteDirectDrive } from '../components/quotes/quoteUtils';
import { useQuoteResultPageStyles } from './QuoteResultPage.styles';

const QuoteResultPage = () => {
  const { styles: modalPageStyles } = useModalPageStyles();
  const { styles } = useQuoteResultPageStyles(modalPageStyles);
  const location = useLocation();
  const { referenceNo } = useParams();
  const { message } = App.useApp();
  const { formatMessage } = useIntl();
  const { showQuoteExpirationMessage } = useQuoteExpirationMessage();
  const { showQuoteDisclaimer } = useQuoteMessage();
  const { data, loading, error } = useQuery(QUOTE_DETAIL_QUERY, {
    variables: { referenceNo },
    skip: !referenceNo,
  });
  const indicator = renderDataStateIndicator({ data, loading, error });
  const quoteDetail = data?.getCustomerQuote || {};
  const { xs, sm, md, lg } = useResponsiveQueries();
  const isMd = xs || sm || md || lg;

  const isDriveQuote = isQuoteDirectDrive(quoteDetail);

  useEffect(() => {
    let expirationKey;
    let disclaimerKey;
    if (location?.state?.newQuote && quoteDetail?.expiryDateTime) {
      expirationKey = showQuoteExpirationMessage(
        formatMessage,
        moment(quoteDetail?.expiryDateTime),
        quoteDetail?.referenceNo
      );

      if (!loading && !isDriveQuote && !location?.state?.phase2Quote)
        disclaimerKey = showQuoteDisclaimer(formatMessage);
    }
    return () => {
      message.destroy(expirationKey);
      message.destroy(disclaimerKey);
    };
  }, [
    quoteDetail?.expiryDateTime,
    quoteDetail?.referenceNo,
    location?.state?.newQuote,
    location?.state?.phase2Quote,
    formatMessage,
    showQuoteExpirationMessage,
    showQuoteDisclaimer,
    message,
    loading,
    isDriveQuote,
  ]);

  return (
    <ModalOverlay>
      <ModalPage
        id="QuoteResultPage"
        className={styles.quoteResultPage}
        wrapInScrollbar
      >
        <OverrideSettingsContextProvider unitSystem={quoteDetail.unitSystem}>
          {indicator || (
            <>
              <QuoteSummaryHeader
                referenceNo={referenceNo}
                quote={quoteDetail}
                quoteTitle={
                  lg
                    ? 'book.newQuote.resultTitle-short'
                    : 'book.newQuote.resultTitle'
                }
              />
              {isMd ? (
                <QuoteContentSmall quoteDetail={quoteDetail} />
              ) : (
                <QuoteContentLarge quoteDetail={quoteDetail} />
              )}
            </>
          )}
        </OverrideSettingsContextProvider>
      </ModalPage>
    </ModalOverlay>
  );
};

export default QuoteResultPage;
